import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuilderComponent, builder } from '@builder.io/react';
import { BUILDER_API_KEY, BUILDER_FOOTER_ABOUT_US_BLOCK_IDS } from '../../config/builder';
import { patchBlockLinks, type BuilderContent } from '../../utils/builderContent';
import { patchAboutUsAnchors, interceptBuilderLinks } from '../../utils/builderLinkRewrites';

const APP_TITLE = 'PRAIRI';

interface BuilderFooterProps {
  urlPath?: string;
  model?: string;
  fallback?: React.ReactNode;
}

const BuilderFooter: React.FC<BuilderFooterProps> = ({
  urlPath = '/investor-footer',
  model = 'page',
  fallback = null,
}) => {
  const [content, setContent] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const initDone = useRef(false);
  const containerRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const savedTitleRef = useRef(
    document.title === 'React App' || !document.title ? APP_TITLE : document.title
  );

  useEffect(() => {
    let cancelled = false;

    const fetchContent = async () => {
      setLoading(true);
      setError(false);
      try {
        if (!initDone.current) {
          builder.init(BUILDER_API_KEY);
          initDone.current = true;
        }

        const builderContentRaw = await builder
          .get(model, {
            url: urlPath,
            userAttributes: { urlPath },
          })
          .promise();

        const builderContent = patchBlockLinks(
          builderContentRaw as BuilderContent,
          BUILDER_FOOTER_ABOUT_US_BLOCK_IDS,
          '/about-us'
        );

        if (!cancelled) setContent(builderContent);
      } catch (err) {
        console.error('Error fetching Builder footer:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchContent();
    return () => {
      cancelled = true;
    };
  }, [urlPath, model]);

  useEffect(() => {
    return interceptBuilderLinks(containerRef.current, navigate);
  }, [navigate, content, loading, error]);

  useEffect(() => {
    if (loading || error || !content || !containerRef.current) return;

    patchAboutUsAnchors(containerRef.current);
  }, [loading, error, content]);

  // Builder.io footer content sets document.title from its CMS entry (e.g. "Footer").
  // Restore the page title so the browser tab stays correct on every route.
  useEffect(() => {
    if (loading || error || !content) return;

    const builderTitle = (content as BuilderContent).data?.title as string | undefined;
    if (!builderTitle) return;

    const restoreTitle = () => {
      if (document.title === builderTitle) {
        document.title = savedTitleRef.current;
      }
    };

    restoreTitle();

    const titleEl = document.querySelector('title');
    if (!titleEl) return;

    const observer = new MutationObserver(restoreTitle);
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });

    return () => observer.disconnect();
  }, [loading, error, content]);

  if (loading) return null;

  if (error || !content) return <>{fallback}</>;

  // Capture the current page title right before Builder can overwrite it.
  if (document.title !== (content as BuilderContent).data?.title) {
    savedTitleRef.current =
      document.title === 'React App' || !document.title ? APP_TITLE : document.title;
  }

  return (
    <footer ref={containerRef} className="mt-auto w-full">
      <BuilderComponent model={model} content={content} inlineContent />
    </footer>
  );
};

export default BuilderFooter;
